/**
 * Afriqahome API Client
 *
 * En fetch-wrapper för Supabase REST API.
 * Kan enkelt bytas ut mot supabase-js när det fungerar.
 */

// Environment variables - set in .env file (VITE_ prefix required for Vite)
// These may be undefined during SSR/prerender - validated at runtime when used
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

// Validate at runtime when API is actually used (not at module load time)
function validateEnv(): { url: string; key: string } {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Missing Supabase environment variables. ' +
      'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
    );
  }
  return { url: SUPABASE_URL, key: SUPABASE_ANON_KEY };
}

export interface ApiResponse<T> {
  data: T | null;
  error: ApiError | null;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: string;
}

interface PaginatedResponse<T> {
  data: T | null;
  error: ApiError | null;
  count: number | null;
}
// Auth response types
export interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  user: AuthUser;
}

export interface AuthUser {
  id: string;
  email: string;
  user_metadata?: Record<string, unknown>;
  app_metadata?: Record<string, unknown>;
}

// Helper to extract error message from unknown error
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error';
}

/**
 * Bas-headers för alla requests
 */
function getHeaders(accessToken?: string): HeadersInit {
  const { key } = validateEnv();
  return {
    'apikey': key,
    'Authorization': `Bearer ${accessToken || key}`,
    'Content-Type': 'application/json',
    'Prefer': 'return=representation'
  };
}

/**
 * GET request till Supabase REST API
 */
export async function apiGet<T>(
  table: string,
  query: string = '',
  accessToken?: string
): Promise<ApiResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/${table}${query ? `?${query}` : ''}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(accessToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ API Error: ${response.status}`, errorData);
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code,
          details: errorData.details
        }
      };
    }

    const data = await response.json();

    return { data, error: null };
  } catch (error: unknown) {
    console.error(`💥 API Exception: ${table}`, error);
    return {
      data: null,
      error: { message: getErrorMessage(error) || 'Network error' }
    };
  }
}

/**
 * GET request with pagination support
 * Returns data along with total count from Content-Range header
 */
export async function apiGetPaginated<T>(
  table: string,
  query: string = '',
  accessToken?: string
): Promise<PaginatedResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/${table}${query ? `?${query}` : ''}`;

  try {
    const headers = getHeaders(accessToken);
    // Add count=exact to get total count in Content-Range header
    (headers as Record<string, string>)['Prefer'] = 'count=exact';

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code,
          details: errorData.details
        },
        count: null
      };
    }

    // Parse Content-Range header for total count
    // Format: "0-19/150" means items 0-19 of 150 total
    const contentRange = response.headers.get('Content-Range');
    let count: number | null = null;
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)/);
      if (match) {
        count = parseInt(match[1], 10);
      }
    }

    const data = await response.json();

    return { data, error: null, count };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Network error';
    return {
      data: null,
      error: { message },
      count: null
    };
  }
}

/**
 * POST request till Supabase REST API
 */
export async function apiPost<T>(
  table: string,
  body: object,
  accessToken?: string
): Promise<ApiResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/${table}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(accessToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ API Error: ${response.status}`, errorData);
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code,
          details: errorData.details
        }
      };
    }

    const data = await response.json();

    return { data, error: null };
  } catch (error: unknown) {
    console.error(`💥 API Exception: ${table}`, error);
    return {
      data: null,
      error: { message: getErrorMessage(error) || 'Network error' }
    };
  }
}

/**
 * RPC call till Supabase (PostgREST `/rest/v1/rpc/<fn>`).
 *
 * Use this for SECURITY DEFINER functions that gate on `auth.uid()`: pass the
 * caller's access token so the JWT (and therefore auth.uid()) reaches Postgres.
 * Calling such a function via supabase-js is NOT equivalent here — this app
 * stores its session under custom localStorage keys, so the supabase-js client
 * often has no session and would send an anonymous (auth.uid() = null) request.
 */
export async function apiRpc<T>(
  fn: string,
  args: object = {},
  accessToken?: string
): Promise<ApiResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/rpc/${fn}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(accessToken),
      body: JSON.stringify(args)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code,
          details: errorData.details
        }
      };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error: unknown) {
    return {
      data: null,
      error: { message: getErrorMessage(error) || 'Network error' }
    };
  }
}

/**
 * PATCH request till Supabase REST API
 */
export async function apiPatch<T>(
  table: string,
  query: string,
  body: object,
  accessToken?: string
): Promise<ApiResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/${table}?${query}`;

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getHeaders(accessToken),
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code
        }
      };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error: unknown) {
    return {
      data: null,
      error: { message: getErrorMessage(error) || 'Network error' }
    };
  }
}

/**
 * UPSERT request till Supabase REST API
 * Uses the on_conflict parameter to handle duplicates
 */
export async function apiUpsert<T>(
  table: string,
  body: object,
  onConflict: string = 'id',
  accessToken?: string
): Promise<ApiResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/${table}`;

  try {
    const headers = getHeaders(accessToken);
    // Add upsert header for conflict resolution
    (headers as Record<string, string>)['Prefer'] = 'return=representation,resolution=merge-duplicates';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Prefer': 'return=representation,resolution=merge-duplicates'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ API Error: ${response.status}`, errorData);
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code,
          details: errorData.details
        }
      };
    }

    const data = await response.json();

    return { data, error: null };
  } catch (error: unknown) {
    console.error(`💥 API Exception: ${table}`, error);
    return {
      data: null,
      error: { message: getErrorMessage(error) || 'Network error' }
    };
  }
}

/**
 * DELETE request till Supabase REST API
 */
export async function apiDelete<T>(
  table: string,
  query: string,
  accessToken?: string
): Promise<ApiResponse<T>> {
  const { url: baseUrl } = validateEnv();
  const url = `${baseUrl}/rest/v1/${table}?${query}`;

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getHeaders(accessToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: {
          message: errorData.message || `HTTP ${response.status}`,
          code: errorData.code
        }
      };
    }

    const data = await response.json().catch(() => null);
    return { data, error: null };
  } catch (error: unknown) {
    return {
      data: null,
      error: { message: getErrorMessage(error) || 'Network error' }
    };
  }
}

/**
 * Supabase Auth API
 */
export const authApi = {
  /**
   * Registrera ny användare
   */
  async signUp(email: string, password: string): Promise<ApiResponse<AuthSession>> {
    const { url: baseUrl, key } = validateEnv();
    const url = `${baseUrl}/auth/v1/signup`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': key,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { data: null, error: { message: data.error_description || data.msg || 'Signup failed' } };
      }

      return { data, error: null };
    } catch (error: unknown) {
      return { data: null, error: { message: getErrorMessage(error) } };
    }
  },

  /**
   * Logga in användare
   */
  async signIn(email: string, password: string): Promise<ApiResponse<AuthSession>> {
    const { url: baseUrl, key } = validateEnv();
    const url = `${baseUrl}/auth/v1/token?grant_type=password`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': key,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { data: null, error: { message: data.error_description || data.msg || 'Login failed' } };
      }

      return { data, error: null };
    } catch (error: unknown) {
      return { data: null, error: { message: getErrorMessage(error) } };
    }
  },

  /**
   * Hämta användare från access token
   */
  async getUser(accessToken: string): Promise<ApiResponse<AuthUser>> {
    const { url: baseUrl, key } = validateEnv();
    const url = `${baseUrl}/auth/v1/user`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'apikey': key,
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        return { data: null, error: { message: 'Invalid token' } };
      }

      return { data, error: null };
    } catch (error: unknown) {
      return { data: null, error: { message: getErrorMessage(error) } };
    }
  },

  /**
   * Logga ut (client-side, radera token)
   */
  signOut(): void {
    localStorage.removeItem('afriqahome_access_token');
    localStorage.removeItem('afriqahome_refresh_token');
    localStorage.removeItem('afriqahome_user');
  }
};
